import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Shield, ChartPie, TrendingUpDown, ChartNoAxesColumn, Award, FileText, ChevronDown } from 'lucide-react';
import { locationTypes, getLocationOptions, getStateAbbreviation, getCountiesForState, getCitiesForState, handleLocationSelect } from '../../constants/locations';
import { getLocationFootnotesMap } from '../../utils/locationFootnotes';
import FootnotesModal from '../Modals/FootnotesModal';
import './AnalysisHeader.css';

const FOOTNOTE_REF_FONT_PX = 13;

const AutoResizeText = ({ text, footnoteNumbers = [], handleFootnoteClick }) => {
  const getInitialFontSize = () => {
    if (window.innerWidth <= 767) return 18;
    if (window.innerWidth <= 1023) return 21;
    return 24;
  };

  const containerRef = useRef(null);
  const textRef = useRef(null);
  const measureRef = useRef(null);
  const lastWindowWidth = useRef(window.innerWidth);
  const lastOrientation = useRef(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');

  const [fontSize, setFontSize] = useState(() => getInitialFontSize());
  const [displayText, setDisplayText] = useState(text);
  const [isTwoLine, setIsTwoLine] = useState(false);

  const getSuffix = useCallback(() => {
    const lastCommaIndex = text.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
      return text.substring(lastCommaIndex + 1);
    }
    return '';
  }, [text]);

  const getTextWithComma = useCallback(() => {
    const lastCommaIndex = text.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
      return text.substring(0, lastCommaIndex + 1);
    }
    return text;
  }, [text]);

  const getFootnoteHtml = useCallback(() => {
    if (footnoteNumbers.length === 0) return '';
    const footnoteSize = FOOTNOTE_REF_FONT_PX;
    const nums = footnoteNumbers.map((num, i) => {
      const comma = i < footnoteNumbers.length - 1
        ? '<span style="margin-right: 1px;">,</span>'
        : '';
      return num + comma;
    }).join('');
    return '<sup style="font-size: ' + footnoteSize + 'px; white-space: nowrap;">' + nums + '</sup>';
  }, [footnoteNumbers]);

  const getSuffixHtml = useCallback((size) => {
    const suffix = getSuffix();
    const footnoteHtml = getFootnoteHtml();
    if (!suffix && !footnoteHtml) return '';
    return '<span style="white-space: nowrap;">' + suffix + footnoteHtml + '</span>';
  }, [getSuffix, getFootnoteHtml]);

  const calculateFontSize = useCallback(() => {
    if (!containerRef.current || !textRef.current || !measureRef.current) return 12;

    const container = containerRef.current;
    const textElement = textRef.current;
    const measureEl = measureRef.current;
    
    const header = container.closest('.analysis-header');
    if (!header) return 12;
    
    const headerWidth = header.clientWidth;
    const isMobile = window.innerWidth <= 767;
    const buttonSpace = isMobile ? (65 + 83 + 56 + 5) : (76 + 91 + 56 + 10);
    const baseAvailableWidth = headerWidth - buttonSpace;
    
    let maxFontSize = getInitialFontSize();
    
    const resetMeasureEl = () => {
      measureEl.style.cssText = 'position: absolute; visibility: hidden; pointer-events: none; left: -9999px;';
    };

    textElement.style.width = '';
    textElement.style.whiteSpace = 'nowrap';

    const textWithComma = getTextWithComma();
    const suffix = getSuffix();
    
    for (let size = maxFontSize; size >= 12; size--) {
      resetMeasureEl();
      measureEl.style.fontSize = `${size}px`;
      measureEl.style.whiteSpace = 'nowrap';
      measureEl.style.width = 'auto';
      measureEl.innerHTML = textWithComma + getSuffixHtml(size);
      
      void measureEl.offsetWidth;
      
      if (measureEl.scrollWidth <= baseAvailableWidth) {
        textElement.style.whiteSpace = 'nowrap';
        textElement.style.width = '';
        setDisplayText(text);
        setIsTwoLine(false);
        return size;
      }
    }
    
    const lineHeight = 12 * 1.2;
    const maxHeight = lineHeight * 2;
    
    const measureWrappedHeight = (htmlContent, width) => {
      resetMeasureEl();
      measureEl.style.fontSize = '12px';
      measureEl.style.whiteSpace = 'normal';
      measureEl.style.wordWrap = 'break-word';
      measureEl.style.wordBreak = 'break-word';
      measureEl.style.overflowWrap = 'break-word';
      measureEl.style.width = `${width}px`;
      measureEl.style.lineHeight = '1.2';
      measureEl.innerHTML = htmlContent;
      void measureEl.offsetWidth;
      return measureEl.scrollHeight;
    };
    
    const suffixHtml = getSuffixHtml(12);
    const fullContent = textWithComma + suffixHtml;
    
    const fullHeight = measureWrappedHeight(fullContent, baseAvailableWidth);
    
    if (fullHeight <= maxHeight + 2) {
      const isActuallyTwoLines = fullHeight > lineHeight + 2;
      
      textElement.style.whiteSpace = 'normal';
      textElement.style.wordWrap = 'break-word';
      textElement.style.wordBreak = 'break-word';
      textElement.style.overflowWrap = 'break-word';
      
      if (isActuallyTwoLines) {
        const range = document.createRange();
        range.selectNodeContents(measureEl);
        const rect = range.getBoundingClientRect();
        textElement.style.width = `${Math.min(Math.ceil(rect.width), baseAvailableWidth)}px`;
      } else {
        textElement.style.width = '';
      }
      
      setDisplayText(text);
      setIsTwoLine(isActuallyTwoLines);
      return 12;
    }
    
    const textBeforeComma = textWithComma.slice(0, -1);
    const ellipsis = '…';
    
    for (let i = textBeforeComma.length - 1; i >= 1; i--) {
      const truncated = textBeforeComma.substring(0, i).trim() + ellipsis + ',' + suffixHtml;
      const height = measureWrappedHeight(truncated, baseAvailableWidth);
      
      if (height <= maxHeight + 2) {
        const finalText = textBeforeComma.substring(0, i).trim() + ellipsis + ',' + suffix;
        
        textElement.style.whiteSpace = 'normal';
        textElement.style.wordWrap = 'break-word';
        textElement.style.wordBreak = 'break-word';
        textElement.style.overflowWrap = 'break-word';
        
        const range = document.createRange();
        range.selectNodeContents(measureEl);
        const rect = range.getBoundingClientRect();
        textElement.style.width = `${Math.min(Math.ceil(rect.width), baseAvailableWidth)}px`;
        
        setDisplayText(finalText);
        setIsTwoLine(true);
        return 12;
      }
    }
    
    textElement.style.whiteSpace = 'normal';
    textElement.style.width = '';
    setDisplayText(ellipsis + ',' + suffix);
    setIsTwoLine(true);
    return 12;
  }, [text, getTextWithComma, getSuffix, getSuffixHtml]);

  useLayoutEffect(() => {
    setFontSize(calculateFontSize());
    
    const handleResize = () => {
      const currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
      const widthChanged = window.innerWidth !== lastWindowWidth.current;
      const orientationChanged = currentOrientation !== lastOrientation.current;
      
      if (widthChanged || orientationChanged) {
        lastWindowWidth.current = window.innerWidth;
        lastOrientation.current = currentOrientation;
        
        const timeout = orientationChanged ? 100 : 0;
        setTimeout(() => setFontSize(calculateFontSize()), timeout);
      }
    };

    const handleOrientationChange = () => {
      setTimeout(() => {
        lastWindowWidth.current = window.innerWidth;
        lastOrientation.current = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
        setFontSize(calculateFontSize());
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [text, footnoteNumbers, calculateFontSize]);

  const footnoteSize = FOOTNOTE_REF_FONT_PX;
  const lastCommaIndex = displayText.lastIndexOf(',');
  const displayWithComma = lastCommaIndex !== -1 ? displayText.substring(0, lastCommaIndex + 1) : displayText;
  const displaySuffix = lastCommaIndex !== -1 ? displayText.substring(lastCommaIndex + 1) : '';

  return (
    <div 
      ref={containerRef} 
      className="auto-resize-container"
    >
      <span
        ref={measureRef}
        aria-hidden="true"
        className="measure-element"
      />
      <div 
        ref={textRef}
        style={{ 
          fontSize: `${fontSize}px`,
          lineHeight: 1.2,
          textAlign: 'center',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'none',
          display: 'inline-block'
        }}
      >
        {displayWithComma}
        <span className="location-suffix">
          {displaySuffix}
          {footnoteNumbers.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              className={`footnotes-button ${isTwoLine ? 'footnotes-two-line' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleFootnoteClick();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFootnoteClick();
                }
              }}
              title="View footnotes"
            >
              {footnoteNumbers.map((num, index) => (
                <React.Fragment key={num}>
                  <sup
                    className="footnote-superscript"
                    style={{ fontSize: `${footnoteSize}px` }}
                  >
                    {num}
                  </sup>
                  {index < footnoteNumbers.length - 1 && (
                    <sup
                      className="footnote-comma"
                      style={{ fontSize: `${footnoteSize}px` }}
                    >
                      ,
                    </sup>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const AnalysisHeader = ({ 
  activeTab, 
  selectedLocation,
  onLocationSelect,
  hasAlertForCurrentLocation,
  onSetAlert
}) => {
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openStateSubmenu, setOpenStateSubmenu] = useState(null);
  const [isFootnotesModalOpen, setIsFootnotesModalOpen] = useState(false);
  const buttonRef = useRef(null);

  const formatLocationName = (locationInfo) => {
    if (!locationInfo?.name) return 'Select Location';
    
    if (locationInfo.type === 'county' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const county = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${county}, ${stateAbbr}`;
    }
    
    if (locationInfo.type === 'city' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const city = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${city}, ${stateAbbr}`;
    }
    
    return locationInfo.name;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('.dropdown-menu-portal');
      const isClickInsideLocation = event.target.closest('.analysis-location');
      
      if (!isClickInsideDropdown && !isClickInsideLocation) {
        setIsLocationOpen(false);
        setOpenSubmenu(null);
        setOpenStateSubmenu(null);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isLocationOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isLocationOpen]);

  const closeMenus = () => {
    setIsLocationOpen(false);
    setOpenSubmenu(null);
    setOpenStateSubmenu(null);
  };

  const handleFilterLocationSelect = (location, type) => {
    const isAggregateSelection = location.label && (
      location.label.includes('All ') || 
      location.isTypeView || 
      location.isAllStateCounties || 
      location.isAllStateCities
    );
    
    if (!isAggregateSelection) {
      const locationWithFlag = {
        ...location,
        info: {
          ...location.info,
          fromHeader: true
        }
      };
      handleLocationSelect(locationWithFlag, type, onLocationSelect, closeMenus);
    } else {
      handleLocationSelect(location, type, onLocationSelect, closeMenus);
    }
  };

  const getTitle = () => {
    switch(activeTab) {
      case 'reports':
        return 'Report';
      case 'totals':
        return 'Total';
      case 'trends':
        return 'Trend';
      case 'rates':
        return 'Rate';
      case 'scores':
        return 'Score';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch(activeTab) {
      case 'reports':
        return <FileText size={32} />;
      case 'totals':
        return <ChartPie size={32} />;
      case 'trends':
        return <TrendingUpDown size={32} />;
      case 'rates':
        return <ChartNoAxesColumn size={32} />;
      case 'scores':
        return <Award size={32} />;
      default:
        return null;
    }
  };

  const getFootnotes = () => getLocationFootnotesMap(selectedLocation);

  const getFootnoteNumbers = () => {
    const footnotes = getFootnotes();
    if (!footnotes) return [];
    
    const numbers = [];
    Object.keys(footnotes).forEach(key => {
      const num = parseInt(key.replace('footnote_', ''));
      numbers.push(num);
    });
    
    return numbers.sort((a, b) => a - b);
  };

  const handleFootnoteClick = () => {
    setIsFootnotesModalOpen(true);
    setIsLocationOpen(false);
    setOpenSubmenu(null);
    setOpenStateSubmenu(null);
  };

  return (
    <div className="analysis-header">
      <div className="analysis-icon">
        {getIcon()}
      </div>
      <div className="analysis-header-content">
        <div className="logo analysis-logo">
          <div className="logo-shield analysis-logo-shield">
            <Shield size={50} />
            <span className="logo-text-k analysis-logo-text-k">K</span>
          </div>
          <span>RIME</span>
          <span>{getTitle()}</span>
        </div>
        <div className="analysis-location custom-dropdown">
          <div 
            ref={buttonRef}
            className={`location-selector ${isLocationOpen ? 'active' : ''}`}
            onClick={() => setIsLocationOpen(!isLocationOpen)}
          >
            <div className="location-title-container">
              <AutoResizeText
                text={formatLocationName(selectedLocation.info)}
                footnoteNumbers={getFootnoteNumbers()} 
                handleFootnoteClick={handleFootnoteClick}
              />
            </div>
            <ChevronDown size={20} className="chevron-down" />
          </div>
          {isLocationOpen && buttonRef.current && createPortal(
            <div 
              className="dropdown-menu-portal" 
              style={{
                position: 'absolute',
                top: `${buttonRef.current.getBoundingClientRect().bottom + window.scrollY + 14}px`,
                left: `${buttonRef.current.getBoundingClientRect().left + (buttonRef.current.getBoundingClientRect().width / 2) + window.scrollX}px`,
                transform: 'translateX(-50%)',
                zIndex: 9999999
              }}
            >
              {locationTypes.map(type => (
                <div key={type.value} className="menu-section">
                  <div 
                    className={`menu-item has-submenu ${openSubmenu === type.value ? 'open' : ''}
                      ${selectedLocation.info?.type === type.value ? 'has-selection' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenSubmenu(openSubmenu === type.value ? null : type.value);
                      setOpenStateSubmenu(null);
                    }}
                  >
                    <span>{type.label}</span>
                    <ChevronDown size={16} />
                  </div>
                  {openSubmenu === type.value && (
                    <div className="submenu">
                      {getLocationOptions(type.value).map(location => {
                        if (type.value === 'county' && location.hasSubmenu) {
                          return (
                            <div key={location.value}>
                              <div 
                                className={`menu-item has-submenu ${openStateSubmenu === location.state ? 'open' : ''}
                                  ${selectedLocation.info?.state === location.state && selectedLocation.info?.type === 'county' && 
                                    (selectedLocation.info?.name?.startsWith('All ') || selectedLocation.info?.name?.includes(' County')) ? 'has-selection' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenStateSubmenu(openStateSubmenu === location.state ? null : location.state);
                                }}
                              >
                                <span>{location.label}</span>
                                <ChevronDown size={16} />
                              </div>
                              {openStateSubmenu === location.state && (
                                <div className="state-counties-submenu">
                                  {getCountiesForState(location.state).map(county => {
                                    let isSelected = false;
                                    
                                    if (county.isAllStateCounties) {
                                      const currentName = (selectedLocation.info?.name || '').replace(/[-]/g, '').trim();
                                      const stateName = county.state;
                                      const stateAbbr = county.stateAbbr;
                                      
                                      isSelected = 
                                        currentName === `All ${stateName} Counties`.trim() || 
                                        currentName === `All ${stateAbbr} Counties`.trim() ||
                                        currentName === `${stateName} Counties`.trim();
                                    } else {
                                      isSelected = selectedLocation.info?.name === county.fullLabel;
                                    }
                                    
                                    return (
                                      <div 
                                        key={county.value} 
                                        className={`menu-item ${isSelected ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isSelected) {
                                            handleFilterLocationSelect(county, 'county');
                                          }
                                        }}
                                      >
                                        {county.isAllStateCounties ? county.label : county.displayLabel}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (type.value === 'city' && location.hasSubmenu) {
                          return (
                            <div key={location.value}>
                              <div 
                                className={`menu-item has-submenu ${openStateSubmenu === location.state ? 'open' : ''}
                                  ${selectedLocation.info?.state === location.state && selectedLocation.info?.type === 'city' && 
                                    (selectedLocation.info?.name?.startsWith('All ') || selectedLocation.info?.name?.includes(',')) ? 'has-selection' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenStateSubmenu(openStateSubmenu === location.state ? null : location.state);
                                }}
                              >
                                <span>{location.label}</span>
                                <ChevronDown size={16} />
                              </div>
                              {openStateSubmenu === location.state && (
                                <div className="state-cities-submenu">
                                  {getCitiesForState(location.state).map(city => {
                                    let isSelected = false;
                                    
                                    if (city.isAllStateCities) {
                                      const currentName = (selectedLocation.info?.name || '').replace(/[-]/g, '').trim();
                                      const stateName = city.state;
                                      const stateAbbr = city.stateAbbr;
                                      
                                      isSelected = 
                                        currentName === `All ${stateName} Cities`.trim() || 
                                        currentName === `All ${stateAbbr} Cities`.trim() ||
                                        currentName === `${stateName} Cities`.trim();
                                    } else {
                                      isSelected = selectedLocation.info?.name === city.fullLabel;
                                    }
                                    
                                    return (
                                      <div 
                                        key={city.value} 
                                        className={`menu-item ${isSelected ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isSelected) {
                                            handleFilterLocationSelect(city, 'city');
                                          }
                                        }}
                                      >
                                        {city.isAllStateCities ? city.label : city.displayLabel}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        return (
                          <div 
                            key={location.value} 
                            className={`menu-item ${
                              `- ${selectedLocation.info?.name} -` === location.label || 
                              selectedLocation.info?.name === location.label
                                ? 'disabled' 
                                : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!(`- ${selectedLocation.info?.name} -` === location.label || 
                                    selectedLocation.info?.name === location.label)) {
                                handleFilterLocationSelect(location, type.value);
                              }
                            }}
                          >
                            {location.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>
      </div>
      <button
        className={`analysis-alert-button${hasAlertForCurrentLocation ? ' alert-active' : ''}`}
        onClick={onSetAlert}
        disabled={!selectedLocation?.info?.name}
        title={hasAlertForCurrentLocation ? 'Manage alert for this location' : 'Set an alert for this location'}
      >
        <div className="text-container">
          {hasAlertForCurrentLocation ? (
            <>
              <span>Alert</span>
              <span>Active</span>
            </>
          ) : (
            <>
              <span>Set</span>
              <span>Alert</span>
            </>
          )}
        </div>
      </button>
      
      <FootnotesModal
        isOpen={isFootnotesModalOpen}
        onClose={() => setIsFootnotesModalOpen(false)}
        footnotes={getFootnotes()}
      />
    </div>
  );
};

export default AnalysisHeader;